import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { useGetPropertiesQuery, useGetVerifiedProfessionalsQuery } from '../../features/api/apiSlice';

const formatCount = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString();
};

const isTakenListing = (property) =>
  property?.taken === true || property?.taken === 1 || property?.taken === '1';

const AboutUs = () => {
  const { data: propertiesRes, isLoading: listingsLoading } = useGetPropertiesQuery({
    limit: 500,
    offset: 0,
  });
  const { data: professionalsRes, isLoading: brokersLoading } = useGetVerifiedProfessionalsQuery();

  const listingCount = propertiesRes?.total_count ?? 0;
  const matchCount = useMemo(
    () => (propertiesRes?.data || []).filter(isTakenListing).length,
    [propertiesRes]
  );
  const brokerCount = professionalsRes?.total ?? professionalsRes?.data?.length ?? 0;

  const stats = [
    // { number: listingsLoading ? '—' : formatCount(listingCount), label: 'Verified Listings' },
    // { number: listingsLoading ? '—' : formatCount(matchCount), label: 'Successful Matches' },
    // { number: brokersLoading ? '—' : formatCount(brokerCount), label: 'Verified Agents' },
     { number: '24/7', label: 'Broker Supervision' },
  ];

  const pillars = [
    {
      title: 'Rental hunting with a HireBroker-held fee',
      description: 'In the app, you request a rental, choose the area and inspection day, and pay the full brokerage fee to HireBroker. HireBroker holds that fee, covers the realtor’s transport from it, and pays the realtor only after you accept the property.'
    },
    {
      title: 'Acquiring Premium Land',
      description: 'Skip the stress of land fraud. We bypass the freelance "street brokers" and connect you directly with vetted, legally cleared land brokers to ensure your investment is secure and properly documented.'
    },
    {
      title: 'Selling Property Seamlessly',
      description: 'Position your land or rental asset in front of qualified buyers. We match your property with the region’s top-performing brokers who actually have ready clients, closing deals in record time.'
    }
  ];

  const steps = [
    {
      tag: '01',
      title: 'Request a rental and pay HireBroker',
      description: 'In the app, say where you want the rental and the day you want to inspect. You pay the full brokerage fee to HireBroker, not to the realtor.'
    },
    {
      tag: '02',
      title: 'Get assigned a realtor',
      description: 'HireBroker assigns a property realtor. Transport for inspections is covered from the fee you already paid.'
    },
    {
      tag: '03',
      title: 'Accept, rematch, or get the balance back',
      description: 'If you accept the property, HireBroker pays the realtor the remaining fee. If you have an issue with the realtor or do not want the properties shown, HireBroker verifies the issue and either assigns another realtor or refunds the remaining balance. This hold is for rental hunting only. HireBroker does not hold money for land transactions.'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-24">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                About HireBroker
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-8">
                Your secure gateway to verified <span className="font-serif italic text-neutral-500 block sm:inline">rentals and land.</span>
              </h1>
              <p className="text-lg sm:text-xl text-neutral-500 font-light leading-relaxed max-w-2xl">
                We bridge the gap between property seekers and honest real estate brokers. By introducing transaction security to East African real estate, we ensure you never lose money to a fake agent again.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: STATS MATRIX (Soft Chalk Grey Background for Visual Break) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-4">
              {stats.map((stat, index) => (
                <div key={index} className="sm:border-l sm:border-neutral-200 sm:first:border-l-0 sm:pl-6 first:pl-0">
                  <div className="text-4xl font-light text-red-600 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 3: THE DEEPER EXPLANATION (Pristine White Background) */}
        <section className="bg-white py-24 max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                The Trust Paradigm
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight leading-tight">
                Real estate in East Africa has a trust problem. We are the architectural solution.
              </h2>
            </div>
            
            <div className="lg:col-span-7 space-y-8 text-neutral-500 font-light leading-relaxed text-base sm:text-lg">
              <p>
                In East Africa, finding a rental is often risky because seekers pay unverified street agents before they see a home. HireBroker changes that for <strong className="text-neutral-900 font-medium">rental hunting</strong>.
              </p>
              <p>
                You request a rental in the app, choose the area and inspection day, and pay the full brokerage fee to HireBroker. HireBroker holds that fee. The assigned realtor’s transport comes from it. The remaining fee is paid to the realtor only after you accept the property.
              </p>
              <p>
                If you have an issue with the realtor, or you did not want the properties shown, HireBroker verifies what happened and either assigns another realtor or refunds the remaining balance. HireBroker does not hold money for land transactions. Read the full explanation at <Link to="/how-fees-work" className="text-neutral-900 underline underline-offset-4">How hunting fees work</Link>.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 4: CORE CAPABILITIES (Soft Chalk Grey Background) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-16">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-2">
                What We Do
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight">Our Core Framework</h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-12 lg:gap-8">
              {pillars.map((pillar, index) => (
                <div key={index} className="space-y-4">
                  <h3 className="text-xl font-medium text-neutral-950 tracking-tight border-b border-neutral-200 pb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-neutral-500 font-light leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 5: THE ECOSYSTEM FLOW (Warm Muted Sand/Grey Background) */}
        <section className="bg-neutral-100/70 py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-2">
                  The Execution
                </span>
                <h2 className="text-3xl font-light text-neutral-950 tracking-tight">
                  How a rental hunt is paid.
                </h2>
                <p className="text-sm text-neutral-500 font-light mt-4 leading-relaxed">
                  You pay HireBroker in the app. HireBroker holds the brokerage fee, covers realtor transport from it, and pays the realtor after you accept. Land deals are matched with verified brokers without HireBroker holding the purchase money.
                </p>
              </div>
              
              <div className="lg:col-span-8 space-y-8">
                {steps.map((step, index) => (
                  <div key={index} className="flex gap-6 items-start bg-white p-6 border border-neutral-200/40 shadow-sm">
                    <span className="text-xs font-mono font-bold text-red-600 pt-1">
                      {step.tag}
                    </span>
                    <div>
                      <h3 className="font-medium text-neutral-950 text-base mb-1">{step.title}</h3>
                      <p className="text-sm text-neutral-500 font-light leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* SECTION 6: CTA (Deep Charcoal / Black Background for Powerful End Note) */}
        <section className="bg-neutral-950 text-white py-24">
          <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
            <span className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold block">
              Get Started
            </span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">
              Ready to find your next space safely?
            </h2>
            <p className="text-neutral-400 font-light max-w-xl mx-auto text-sm sm:text-base">
              For rentals, pay HireBroker in the app. HireBroker holds the brokerage fee until you accept. Land listings are matched with verified brokers without HireBroker holding the purchase money.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link
                to="/how-fees-work"
                className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-200"
              >
                How hunting fees work
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border border-neutral-800 text-neutral-300 text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-200"
              >
                Contact us
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;